import { z } from 'zod';
import { resourceKeySchema, timestampSchema } from './common';

/**
 * Access windows: the periods during which work is actually allowed to happen.
 *
 * A great deal of infrastructure work cannot be done whenever the plan says.
 * The thing being worked on is in use the rest of the time, and it is handed
 * over for a negotiated period: a railway possession, a road lane closure, a
 * plant shutdown, a maintenance window on a running system. All four are the
 * same object -- a named span of time, agreed in advance, that work has to fit
 * inside -- and the differences between them are vocabulary.
 *
 * ## What this deliberately does *not* do
 *
 * It does not make the scheduling engine solve for windows. Fitting work into
 * available access is calendar-constrained scheduling, which is a different
 * algorithm from the forward pass this product runs, and adopting it would
 * break the engine's own rule: it never invents a date, and it never reorders
 * work silently to hit one.
 *
 * Instead a window is expressed in the terms the engine already has:
 *
 *  - its **start is a lower bound**, which is exactly what `earliestStart`
 *    already is. Pushing a task forward to the beginning of its window is the
 *    kind of thing the forward pass does anyway.
 *  - its **end is reported, not enforced** -- a warning, exactly like a missed
 *    deadline. The engine says the work does not fit and leaves the decision to
 *    a person, because a window that silently truncated a task would produce a
 *    plan nobody agreed to.
 *
 * A window is therefore a two-sided deadline, and the engine needs to know
 * nothing else about it. It never learns what a possession is, who approves
 * one, or where it is: the API resolves a task's window to two dates and passes
 * those.
 *
 * ## Where a window is
 *
 * By linear reference, when the project has one (contracts:
 * linear-referencing.ts). A possession closes a named stretch of line, and
 * saying which is what lets "what is happening between km 245 and 250 next
 * weekend" be one question rather than two. A project with no linear network
 * still gets windows; they simply have no position, like everything else.
 */

/* -------------------------------------------------------------- lifecycle */

/**
 * Where a window is in its own life.
 *
 * `planned` is somebody's intention, `confirmed` is an agreement with whoever
 * controls the access, and `cancelled` keeps the record of one that was given
 * up. Only a confirmed window is something to plan against, and the engine is
 * told about no other kind -- planning work into a possession nobody has been
 * granted is how a programme becomes fiction.
 *
 * Deliberately not the `ScheduleChangeSession` approval machinery, which looked
 * reusable and is not: that models one person asking the *owners of affected
 * tasks* to accept a change, and a window is granted by a party outside the
 * system entirely. Recording who confirmed it and when is the honest amount of
 * process for something whose real approval happened elsewhere.
 */
export const ACCESS_WINDOW_STATUSES = ['planned', 'confirmed', 'cancelled'] as const;
export const accessWindowStatusSchema = z.enum(ACCESS_WINDOW_STATUSES);
export type AccessWindowStatus = (typeof ACCESS_WINDOW_STATUSES)[number];

/** Only a confirmed window constrains anything. */
export function constrainsSchedule(status: AccessWindowStatus): boolean {
  return status === 'confirmed';
}

/* ----------------------------------------------------------- the record */

export const accessWindowSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  status: accessWindowStatusSchema,

  /**
   * The span itself. Timestamps rather than dates: a possession runs 02:00 to
   * 10:00 on a Saturday, and a window recorded as "Saturday" would be read by
   * everybody as a whole day they do not have.
   */
  startsAt: timestampSchema,
  endsAt: timestampSchema,

  /**
   * The calendar days the span covers in the project's own timezone, which is
   * what the day-granular scheduling engine compares against. Derived by the
   * API rather than stored, so it cannot disagree with the timestamps beside
   * it, and returned so a client renders the same days the engine used.
   */
  startDate: z.string().date(),
  endDate: z.string().date(),

  confirmedByUserId: z.string().uuid().nullable(),
  confirmedAt: timestampSchema.nullable(),

  /** Leaf and summary tasks assigned to this window. */
  taskCount: z.number().int().nonnegative(),

  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type AccessWindow = z.infer<typeof accessWindowSchema>;

const windowWritableFields = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullish(),
  startsAt: timestampSchema,
  endsAt: timestampSchema,
});

/** A window that ends before it opens is not a window. */
function refineSpan<T extends z.ZodTypeAny>(schema: T) {
  return schema.refine(
    (value) => {
      const record = value as { startsAt?: string; endsAt?: string };
      if (!record.startsAt || !record.endsAt) return true;
      return record.startsAt < record.endsAt;
    },
    { message: 'endsAt must be after startsAt', path: ['endsAt'] },
  );
}

export const createAccessWindowSchema = refineSpan(
  windowWritableFields.extend({ key: resourceKeySchema }).strict(),
);
export type CreateAccessWindow = z.infer<typeof createAccessWindowSchema>;

/**
 * `status` is absent, and so is `key`.
 *
 * Confirming or cancelling a window is a decision with a party outside the
 * system behind it, so it has its own route that records who and when. Rolling
 * it into a general edit would let a rename quietly confirm a possession
 * nobody had been granted.
 */
export const updateAccessWindowSchema = refineSpan(
  windowWritableFields.partial().extend({ version: z.number().int().nonnegative() }).strict(),
);
export type UpdateAccessWindow = z.infer<typeof updateAccessWindowSchema>;

export const setAccessWindowStatusSchema = z
  .object({
    status: accessWindowStatusSchema,
    version: z.number().int().nonnegative(),
  })
  .strict();
export type SetAccessWindowStatus = z.infer<typeof setAccessWindowStatusSchema>;

export const accessWindowListQuerySchema = z.object({
  status: accessWindowStatusSchema.optional(),
  /** Windows overlapping this span, for "what access do we have next month". */
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
});
export type AccessWindowListQuery = z.infer<typeof accessWindowListQuerySchema>;

/* --------------------------------------------------------------- warnings */

/**
 * A task assigned to a window whose calculated dates do not fit inside it.
 *
 * Read outside the engine that emits it -- the schedule summary counts it and
 * the task list renders it -- so the code is named here for the reason
 * `DEADLINE_MISSED_WARNING` is: a literal in a query and another in a renderer
 * are two copies of a rule that drift silently.
 */
export const OUTSIDE_WINDOW_WARNING = 'schedule.outside_access_window';

/**
 * The engine's view of a task's window: two dates, and nothing else.
 *
 * Deliberately not the window itself. The engine never learns what an access
 * window is, who confirmed one or where it runs -- it is handed a lower bound
 * and an upper bound, which are shapes it already understands. That is what
 * keeps a scheduling engine free of a domain concept most projects do not have.
 */
export interface ResolvedWindowBounds {
  startDate: string;
  endDate: string;
}
